import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  type RowSelectionState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  ArrowDown,
  ArrowUp,
  Columns3,
  Download,
  Filter as FilterIcon,
  MoreHorizontal,
  RefreshCw,
  Rows3,
  Search,
  X,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { useAuth } from '@/providers/AuthProvider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Menu, MenuItem, MenuLabel } from '@/components/ui/menu'
import { EmptyState, ErrorState, PermissionDenied, TableSkeleton } from '@/components/States'
import { alignOf, cellToText, renderCell } from './cells'
import { FilterChips, FilterPanel } from './FilterPanel'
import { DEFAULT_PAGE_SIZE, PAGE_SIZES, decodeState, encodeState } from './urlState'
import type {
  AdminColumn,
  BulkAction,
  Density,
  Fetcher,
  FilterValue,
  RowAction,
  Selection,
  SortSpec,
} from './types'

const ROW_H: Record<Density, number> = { compact: 28, normal: 36, comfortable: 44 }

export interface DataTableProps<T> {
  /** TZ ID — qabul mezoni (`A3.01`). Audit va debug uchun DOM'ga yoziladi. */
  screenId: string
  columns: AdminColumn<T>[]
  fetcher: Fetcher<T>
  rowId: (row: T) => string
  /** Bu jadvalni ko'rish huquqi. Yo'q bo'lsa <PermissionDenied> ko'rsatiladi. */
  permission?: string

  selectable?: boolean
  bulkActions?: BulkAction<T>[]
  rowActions?: RowAction<T>[]
  onRowClick?: (row: T) => void
  /** O'ngdan 480px panel (SKILL §5) — Space bilan ochiladi. */
  detailPanel?: (row: T) => ReactNode

  emptyState?: { icon?: ReactNode; title?: string; description?: string; action?: ReactNode }
  exportable?: boolean
  searchable?: boolean
  density?: Density
  /** URL prefiksi — bitta sahifada ikki jadval bo'lsa holatlar aralashmasin. */
  urlPrefix?: string
  /** «N ta yangi ↑» banneri uchun. Jadval O'ZI yangilanmaydi (X1). */
  pendingCount?: number
  onShowPending?: () => void
  toolbarExtra?: ReactNode
}

export function DataTable<T>({
  screenId,
  columns,
  fetcher,
  rowId,
  permission,
  selectable = false,
  bulkActions = [],
  rowActions = [],
  onRowClick,
  detailPanel,
  emptyState,
  exportable = false,
  searchable = true,
  density: densityProp,
  urlPrefix = '',
  pendingCount = 0,
  onShowPending,
  toolbarExtra,
}: DataTableProps<T>) {
  const t = useT()
  const { can } = useAuth()

  /* ── Holat: URL manba ────────────────────────────────────────── */
  const [state, setState] = useState(() => {
    const s = decodeState(window.location.search, urlPrefix)
    // URL'da `hide` bo'lmasa — `hiddenByDefault` ustunlari yashiriladi.
    // URL'da bo'lsa u ustun turadi: ulashilgan havola AYNAN o'sha ko'rinishni
    // ochishi kerak, jumladan «default yashirin ustun ochilgan» holatni ham.
    const hidden = window.location.search.includes('hide=')
      ? s.hidden
      : columns.filter((c) => c.hiddenByDefault).map((c) => c.key)
    return { ...s, hidden, density: s.density ?? densityProp ?? ('normal' as Density) }
  })

  // Holat o'zgarsa URL yangilanadi (havola ulashiladigan bo'lib qoladi).
  useEffect(() => {
    const qs = encodeState({ ...state, density: state.density }, urlPrefix)
    const next = `${window.location.pathname}${qs ? `?${qs}` : ''}`
    if (next !== window.location.pathname + window.location.search) {
      window.history.replaceState(null, '', next)
    }
  }, [state, urlPrefix])

  const [rows, setRows] = useState<T[]>([])
  const [total, setTotal] = useState<number | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [selection, setSelection] = useState<RowSelectionState>({})
  const [allMatching, setAllMatching] = useState(false)
  const [activeRow, setActiveRow] = useState(0)
  const [detailRow, setDetailRow] = useState<T | null>(null)

  const searchRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const lastPicked = useRef<number | null>(null)

  /* ── Huquq bo'yicha ustunlarni filtrlash (rbac-ui.md) ────────── */
  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (c) => (!c.permission || can(c.permission)) && !state.hidden.includes(c.key),
      ),
    [columns, can, state.hidden],
  )
  const permittedColumns = useMemo(
    () => columns.filter((c) => !c.permission || can(c.permission)),
    [columns, can],
  )

  /* ── Ma'lumot olish ──────────────────────────────────────────── */
  const { filters, sort, page, pageSize, search } = state

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)

    fetcher({ filters, sort, page, pageSize, search, signal: ac.signal })
      .then((res) => {
        if (!alive) return
        setRows(res.rows)
        setTotal(res.total)
        setActiveRow(0)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })

    return () => {
      alive = false
      ac.abort()
    }
    // `fetcher` chaqiruvchi tomonda useCallback bilan barqarorlashtiriladi.
  }, [fetcher, filters, sort, page, pageSize, search, refreshKey])

  // Filtr o'zgarsa tanlov ma'nosini yo'qotadi.
  useEffect(() => {
    setSelection({})
    setAllMatching(false)
    lastPicked.current = null
  }, [filters, search, page])

  /* ── TanStack Table (TZ §0.3) ────────────────────────────────
     Saralash/filtr/paginatsiya SERVER tomonda — shuning uchun `manual*`. */
  const tanColumns = useMemo<ColumnDef<T>[]>(
    () => visibleColumns.map((c) => ({ id: c.key, accessorKey: c.key })),
    [visibleColumns],
  )

  const table = useReactTable({
    data: rows,
    columns: tanColumns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableRowSelection: selectable,
    state: { rowSelection: selection },
    onRowSelectionChange: setSelection,
    getRowId: (row) => rowId(row),
  })

  const tableRows = table.getRowModel().rows
  const rowHeight = ROW_H[state.density ?? 'normal']

  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  })

  /* ── Holat o'zgartiruvchilar ─────────────────────────────────── */
  const setFilters = useCallback((next: FilterValue[]) => {
    setState((s) => ({ ...s, filters: next, page: 1 }))
  }, [])

  const toggleSort = useCallback((key: string) => {
    setState((s) => {
      const cur = s.sort.find((x) => x.key === key)
      let next: SortSpec[]
      // asc → desc → o'chirilgan
      if (!cur) next = [{ key, desc: false }]
      else if (!cur.desc) next = [{ key, desc: true }]
      else next = []
      return { ...s, sort: next, page: 1 }
    })
  }, [])

  const toggleColumn = useCallback((key: string) => {
    setState((s) => ({
      ...s,
      hidden: s.hidden.includes(key) ? s.hidden.filter((k) => k !== key) : [...s.hidden, key],
    }))
  }, [])

  const selectedIds = useMemo(() => Object.keys(selection).filter((k) => selection[k]), [selection])
  const selectedRows = useMemo(
    () => rows.filter((r) => selection[rowId(r)]),
    [rows, selection, rowId],
  )

  /** `x` — bitta, `Shift+x` — oxirgi tanlangandan diapazon (tables.md). */
  const pickRow = useCallback(
    (index: number, range: boolean) => {
      if (!selectable) return
      const row = tableRows[index]
      if (!row) return
      setSelection((prev) => {
        const next = { ...prev }
        if (range && lastPicked.current !== null) {
          const [a, b] = [lastPicked.current, index].sort((x, y) => x - y) as [number, number]
          const on = !prev[row.id]
          for (let i = a; i <= b; i++) {
            const r = tableRows[i]
            if (r) next[r.id] = on
          }
        } else {
          next[row.id] = !prev[row.id]
        }
        return next
      })
      lastPicked.current = index
      setAllMatching(false)
    },
    [selectable, tableRows],
  )

  /* ── Klaviatura (SKILL §6) ───────────────────────────────────── */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null
      const typing =
        el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)

      if (e.key === '/' && !typing) {
        e.preventDefault()
        searchRef.current?.focus()
        return
      }
      if (typing) return

      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          e.preventDefault()
          setActiveRow((i) => {
            const n = Math.min(i + 1, tableRows.length - 1)
            virtualizer.scrollToIndex(n, { align: 'auto' })
            return n
          })
          break
        case 'k':
        case 'ArrowUp':
          e.preventDefault()
          setActiveRow((i) => {
            const n = Math.max(i - 1, 0)
            virtualizer.scrollToIndex(n, { align: 'auto' })
            return n
          })
          break
        case 'x':
          e.preventDefault()
          pickRow(activeRow, e.shiftKey)
          break
        case 'Enter': {
          const r = tableRows[activeRow]
          if (r && onRowClick) {
            e.preventDefault()
            onRowClick(r.original)
          }
          break
        }
        case ' ': {
          const r = tableRows[activeRow]
          if (r && detailPanel) {
            e.preventDefault()
            setDetailRow(r.original)
          }
          break
        }
        case 'Escape':
          if (detailRow) setDetailRow(null)
          else if (selectedIds.length) {
            setSelection({})
            setAllMatching(false)
          }
          break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeRow, tableRows, pickRow, onRowClick, detailPanel, detailRow, selectedIds.length, virtualizer])

  /* ── Eksport ─────────────────────────────────────────────────
     tables.md: <10k → darhol; >10k → fon job (server kerak — HOLAT.md). */
  // CSV-injection (F52): `=`, `+`, `-`, `@`, TAB, CR bilan boshlangan katak
  // Excel/Sheets'da formula sifatida bajarilmasin — oldiga `'` qo'yiladi.
  const csvCell = (v: string): string => {
    const neutral = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v
    return `"${neutral.replace(/"/g, '""')}"`
  }
  const exportCsv = useCallback(() => {
    const cols = visibleColumns
    const head = cols.map((c) => csvCell(c.header)).join(',')
    const body = rows
      .map((r) => cols.map((c) => csvCell(cellToText(c, r))).join(','))
      .join('\n')
    // UTF-8 BOM — Excel kirill/lotin harflarni to'g'ri ochsin.
    const blob = new Blob([`﻿${head}\n${body}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${screenId}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [visibleColumns, rows, screenId])

  const totalPages = total !== undefined ? Math.max(1, Math.ceil(total / pageSize)) : undefined
  const from = (page - 1) * pageSize + 1
  const to = (page - 1) * pageSize + rows.length

  const currentSelection: Selection<T> | null = allMatching
    ? { mode: 'allMatching', total: total ?? 0, filters, search }
    : selectedRows.length
      ? { mode: 'rows', rows: selectedRows }
      : null

  // rbac-ui.md: huquqsiz kirishda QAYSI huquq kerakligi aytiladi.
  // Tekshiruv barcha hook'lardan KEYIN — React hook tartibi buzilmasin.
  if (permission && !can(permission)) return <PermissionDenied permission={permission} />

  /* ── Ko'rinish ───────────────────────────────────────────────── */
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-screen={screenId}>
      {/* Asboblar paneli */}
      <div className="flex items-center gap-2 border-b border-border-subtle px-3 py-2">
        {searchable && (
          <div className="relative w-72">
            <Search size={13} className="pointer-events-none absolute top-1/2 left-2 -translate-y-1/2 text-tertiary" />
            <Input
              ref={searchRef}
              value={search}
              placeholder={`${t.common.search}  ( / )`}
              onChange={(e) => setState((s) => ({ ...s, search: e.target.value, page: 1 }))}
              className="pl-7"
            />
            {search && (
              <button
                type="button"
                aria-label={t.common.clear}
                onClick={() => setState((s) => ({ ...s, search: '', page: 1 }))}
                className="absolute top-1/2 right-1.5 -translate-y-1/2 text-tertiary hover:text-primary"
              >
                <X size={12} />
              </button>
            )}
          </div>
        )}

        <Button
          variant={filtersOpen || filters.length ? 'primary' : 'default'}
          size="md"
          onClick={() => setFiltersOpen((v) => !v)}
        >
          <FilterIcon size={13} />
          {t.table.filters}
          {filters.length > 0 && <span className="tabular">({filters.length})</span>}
        </Button>

        {toolbarExtra}

        <div className="flex-1" />

        {/* Zichlik */}
        <Menu
          align="end"
          width={180}
          trigger={({ toggle, ref }) => (
            <Button ref={ref} variant="ghost" size="icon" onClick={toggle} title={t.table.density}>
              <Rows3 size={14} />
            </Button>
          )}
        >
          {(close) => (
            <>
              <MenuLabel>{t.table.density}</MenuLabel>
              {(['compact', 'normal', 'comfortable'] as const).map((d) => (
                <MenuItem
                  key={d}
                  onSelect={() => {
                    setState((s) => ({ ...s, density: d }))
                    close()
                  }}
                  trailing={state.density === d ? '✓' : undefined}
                >
                  {d === 'compact'
                    ? t.table.densityCompact
                    : d === 'normal'
                      ? t.table.densityNormal
                      : t.table.densityComfortable}
                </MenuItem>
              ))}
            </>
          )}
        </Menu>

        {/* Ustunlar */}
        <Menu
          align="end"
          width={240}
          trigger={({ toggle, ref }) => (
            <Button ref={ref} variant="ghost" size="icon" onClick={toggle} title={t.table.columns}>
              <Columns3 size={14} />
            </Button>
          )}
        >
          <>
            <MenuLabel>{t.table.columns}</MenuLabel>
            {permittedColumns.map((c) => (
              <label
                key={c.key}
                className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-sm text-primary hover:bg-surface-3"
              >
                <Checkbox checked={!state.hidden.includes(c.key)} onChange={() => toggleColumn(c.key)} />
                <span className="truncate">{c.header}</span>
              </label>
            ))}
          </>
        </Menu>

        {exportable && (
          <Button variant="ghost" size="icon" onClick={exportCsv} title={t.table.export}>
            <Download size={14} />
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setRefreshKey((k) => k + 1)}
          title={t.table.refresh}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : undefined} />
        </Button>
      </div>

      <FilterPanel columns={permittedColumns} filters={filters} onChange={setFilters} open={filtersOpen} />
      <FilterChips columns={permittedColumns} filters={filters} onChange={setFilters} />

      {/* X1: real-time banner — jadval O'ZI yangilanmaydi (o'qiyotgan joy yo'qolmasin) */}
      {pendingCount > 0 && (
        <button
          type="button"
          onClick={onShowPending}
          className="border-b border-border-subtle bg-accent-subtle px-3 py-1.5 text-sm font-medium text-accent hover:bg-surface-3"
        >
          {t.table.newRecords(pendingCount)} · {t.table.showNew}
        </button>
      )}

      {/* Jadval + yon panel */}
      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Sarlavha qatori */}
          <div
            className="flex shrink-0 items-center gap-4 border-b border-border-default bg-surface-2 px-3"
            style={{ height: 32 }}
            role="row"
          >
            {selectable && (
              <Checkbox
                checked={tableRows.length > 0 && selectedIds.length === tableRows.length}
                indeterminate={selectedIds.length > 0 && selectedIds.length < tableRows.length}
                onChange={(e) => {
                  const on = e.target.checked
                  const next: RowSelectionState = {}
                  if (on) for (const r of tableRows) next[r.id] = true
                  setSelection(next)
                  setAllMatching(false)
                }}
                aria-label={t.common.all}
              />
            )}
            {visibleColumns.map((c) => {
              const s = sort.find((x) => x.key === c.key)
              const a = alignOf(c as AdminColumn<unknown>)
              return (
                <div
                  key={c.key}
                  className={cn('min-w-0 flex-1', a === 'right' && 'text-right', a === 'center' && 'text-center')}
                  style={c.width ? { flex: `0 0 ${c.width}px` } : { minWidth: c.minWidth ?? 80 }}
                >
                  <button
                    type="button"
                    disabled={c.sortable === false}
                    onClick={() => toggleSort(c.key)}
                    className={cn(
                      'inline-flex max-w-full items-center gap-1 text-xs font-medium text-tertiary uppercase',
                      c.sortable !== false && 'hover:text-primary',
                      'disabled:cursor-default',
                    )}
                  >
                    <span className="truncate">{c.header}</span>
                    {s && (s.desc ? <ArrowDown size={11} /> : <ArrowUp size={11} />)}
                  </button>
                </div>
              )
            })}
            {rowActions.length > 0 && <div style={{ flex: '0 0 32px' }} />}
          </div>

          {/* Tana */}
          <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
            {loading ? (
              <TableSkeleton rows={Math.min(pageSize, 14)} columns={visibleColumns.length} />
            ) : error ? (
              <ErrorState
                code={error.name === 'ApiError' ? error.message : undefined}
                onRetry={() => setRefreshKey((k) => k + 1)}
              />
            ) : tableRows.length === 0 ? (
              <EmptyState
                {...(emptyState?.icon ? { icon: emptyState.icon } : {})}
                {...(emptyState?.title ? { title: emptyState.title } : {})}
                {...(emptyState?.description ? { description: emptyState.description } : {})}
                {...(emptyState?.action ? { action: emptyState.action } : {})}
              />
            ) : (
              <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
                {virtualizer.getVirtualItems().map((vi) => {
                  const row = tableRows[vi.index]
                  if (!row) return null
                  const data = row.original
                  const isActive = vi.index === activeRow
                  const isSelected = Boolean(selection[row.id])

                  return (
                    <div
                      key={row.id}
                      role="row"
                      aria-selected={isSelected}
                      onClick={() => {
                        setActiveRow(vi.index)
                        onRowClick?.(data)
                      }}
                      className={cn(
                        'group absolute top-0 left-0 flex w-full items-center gap-4 border-b border-border-subtle px-3',
                        'text-base',
                        onRowClick && 'cursor-pointer',
                        isSelected ? 'bg-accent-subtle' : 'hover:bg-surface-3',
                        isActive && 'ring-1 ring-accent ring-inset',
                      )}
                      style={{ height: vi.size, transform: `translateY(${vi.start}px)` }}
                    >
                      {selectable && (
                        <Checkbox
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            pickRow(vi.index, (e.nativeEvent as MouseEvent).shiftKey)
                          }}
                          aria-label={row.id}
                        />
                      )}
                      {visibleColumns.map((c) => {
                        const a = alignOf(c as AdminColumn<unknown>)
                        return (
                          <div
                            key={c.key}
                            className={cn(
                              'min-w-0 flex-1 truncate',
                              a === 'right' && 'text-right',
                              a === 'center' && 'text-center',
                            )}
                            style={c.width ? { flex: `0 0 ${c.width}px` } : { minWidth: c.minWidth ?? 80 }}
                          >
                            {renderCell(c, data)}
                          </div>
                        )
                      })}

                      {/* SKILL §6: amallar oxirgi ustunda, hover'da ko'rinadi */}
                      {rowActions.length > 0 && (
                        <div style={{ flex: '0 0 32px' }} onClick={(e) => e.stopPropagation()}>
                          <Menu
                            align="end"
                            width={200}
                            trigger={({ toggle, ref }) => (
                              <Button
                                ref={ref}
                                variant="ghost"
                                size="icon"
                                onClick={toggle}
                                aria-label={t.common.actions}
                                className="opacity-0 group-hover:opacity-100 focus:opacity-100"
                              >
                                <MoreHorizontal size={14} />
                              </Button>
                            )}
                          >
                            {(close) => (
                              <>
                                {rowActions
                                  .filter((ra) => !ra.permission || can(ra.permission))
                                  .filter((ra) => !ra.visible || ra.visible(data))
                                  .map((ra) => (
                                    <MenuItem
                                      key={ra.id}
                                      {...(ra.icon ? { icon: ra.icon } : {})}
                                      {...(ra.danger ? { danger: true } : {})}
                                      onSelect={() => {
                                        close()
                                        ra.onSelect(data)
                                      }}
                                    >
                                      {ra.label}
                                    </MenuItem>
                                  ))}
                              </>
                            )}
                          </Menu>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Paginatsiya */}
          <div className="flex shrink-0 items-center gap-3 border-t border-border-subtle px-3 py-2 text-sm text-secondary">
            <span className="tabular">
              {total !== undefined ? t.table.rangeLabel(from, Math.max(from, to), total) : `${from}–${to}`}
            </span>
            <div className="flex-1" />
            <label className="flex items-center gap-1.5">
              <span className="text-tertiary">{t.table.rowsPerPage}</span>
              <select
                value={pageSize}
                onChange={(e) => setState((s) => ({ ...s, pageSize: Number(e.target.value), page: 1 }))}
                className="h-6 rounded-sm border border-border-default bg-surface-1 px-1 text-sm text-primary"
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <Button
              size="sm"
              disabled={page <= 1}
              onClick={() => setState((s) => ({ ...s, page: Math.max(1, s.page - 1) }))}
            >
              {t.table.prevPage}
            </Button>
            <span className="tabular text-tertiary">
              {page}
              {totalPages ? ` / ${totalPages}` : ''}
            </span>
            <Button
              size="sm"
              disabled={rows.length < pageSize || (totalPages !== undefined && page >= totalPages)}
              onClick={() => setState((s) => ({ ...s, page: s.page + 1 }))}
            >
              {t.table.nextPage}
            </Button>
          </div>
        </div>

        {/* Yon panel — SKILL §5: o'ngdan 480px, yangi sahifaga o'tmasdan */}
        {detailPanel && detailRow && (
          <aside
            className="flex shrink-0 flex-col overflow-auto border-l border-border-default bg-surface-2"
            style={{ width: 'var(--detail-panel-w)' }}
          >
            <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2">
              <span className="text-sm font-medium text-secondary">{t.common.profile}</span>
              <Button variant="ghost" size="icon" onClick={() => setDetailRow(null)} aria-label={t.common.close}>
                <X size={14} />
              </Button>
            </div>
            <div className="min-h-0 flex-1 p-3">{detailPanel(detailRow)}</div>
          </aside>
        )}
      </div>

      {/* Ommaviy amallar paneli (SKILL §5, 52px) */}
      {selectable && currentSelection && (
        <div
          className="flex shrink-0 items-center gap-3 border-t border-border-default bg-surface-2 px-3"
          style={{ height: 'var(--bulkbar-h)' }}
        >
          <span className="text-base font-medium text-primary tabular">
            {allMatching && total !== undefined
              ? t.table.selectAllMatching(total)
              : t.table.selected(selectedIds.length)}
          </span>

          {/* tables.md: «Filtr bo'yicha hammasi» — sahifa emas, butun natija */}
          {!allMatching && total !== undefined && total > rows.length && (
            <button
              type="button"
              onClick={() => setAllMatching(true)}
              className="text-sm text-accent hover:underline"
            >
              {t.table.selectAllMatching(total)}
            </button>
          )}

          <div className="flex-1" />

          {bulkActions
            .filter((b) => !b.permission || can(b.permission))
            .map((b) => (
              <Button
                key={b.id}
                variant={b.danger ? 'danger' : 'default'}
                size="sm"
                onClick={() => b.onSelect(currentSelection)}
              >
                {b.icon}
                {b.label}
              </Button>
            ))}

          <Button
            variant="ghost"
            size="icon"
            aria-label={t.table.clearSelection}
            onClick={() => {
              setSelection({})
              setAllMatching(false)
            }}
          >
            <X size={14} />
          </Button>
        </div>
      )}
    </div>
  )
}

export { DEFAULT_PAGE_SIZE, PAGE_SIZES }
export type { AdminColumn, BulkAction, Fetcher, RowAction, Selection, Density } from './types'
